import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { Button } from '@/components/ui/button';
import {
  ShieldCheck, BadgeCheck, Eye, MessageSquareWarning,
  Target, Compass, Sparkles, ArrowRight, Store, ShoppingBag,
} from 'lucide-react';

/**
 * AutoMarket Colombia — Institutional "Quiénes somos" page.
 *
 * Copy tone: builds trust without over-promising. Verbs are deliberate
 * ("validamos", "buscamos verificar", "trabajamos para") and avoid legal
 * absolutes so the page stays truthful about what the platform actually
 * enforces today.
 */
export default function QuienesSomos() {
  useSEO({
    title: 'Quiénes somos | AutoMarket Colombia',
    description:
      'Somos una plataforma especializada en repuestos y productos para vehículos en Colombia. Construimos confianza entre compradores y vendedores mediante validación, transparencia y soporte.',
    canonical: '/quienes-somos',
  });

  return (
    <Layout>
      {/* ═══════════════ HERO ═══════════════ */}
      <section
        data-testid="qs-hero"
        className="relative overflow-hidden bg-slate-900 text-white"
      >
        {/* Decorative orange accent */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.25em] text-orange-300">
              <Sparkles className="h-3 w-3" /> AutoMarket Colombia
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              Quiénes somos
            </h1>
            <p className="mt-5 text-lg sm:text-xl text-slate-300 max-w-2xl">
              La plataforma donde comprar y vender repuestos comienza con algo
              fundamental: <span className="text-orange-400 font-semibold">la confianza</span>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/catalogo"
                data-testid="qs-hero-cta-catalog"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold px-6 py-3 rounded-none transition-colors"
              >
                <ShoppingBag className="h-5 w-5" /> Explorar catálogo
              </Link>
              <Link
                to="/vendedor/onboarding"
                data-testid="qs-hero-cta-seller"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-bold px-6 py-3 rounded-none transition-colors"
              >
                <Store className="h-5 w-5" /> Quiero ser vendedor
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ MISSION ═══════════════ */}
      <section data-testid="qs-mission" className="bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 items-start">
            <div>
              <div className="inline-flex items-center gap-2 text-orange-600">
                <Target className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Nuestra misión</span>
              </div>
              <div className="mt-3 h-1 w-16 bg-orange-500" />
            </div>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 leading-tight">
                Construimos un marketplace confiable para el sector automotor colombiano.
              </h2>
              <p className="mt-5 text-slate-700 text-lg leading-relaxed">
                AutoMarket Colombia es una plataforma especializada en la
                comercialización de <b>repuestos y productos para vehículos</b>,
                creada para conectar compradores con vendedores de manera{' '}
                <b>segura, transparente y confiable</b>.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Nuestra misión principal es construir un marketplace donde los
                usuarios puedan comprar con tranquilidad, sabiendo que detrás
                de cada vendedor existe un proceso de validación y que
                trabajamos para reducir al máximo los riesgos de fraude,
                vendedores ficticios, productos de procedencia dudosa o
                información engañosa.
              </p>
              <blockquote className="mt-8 border-l-4 border-orange-500 bg-slate-50 pl-5 py-3">
                <p className="text-slate-900 font-display text-xl italic">
                  “Más que conectar compradores y vendedores, construimos confianza.”
                </p>
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ DIFFERENTIATORS ═══════════════ */}
      <section data-testid="qs-differentiators" className="bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 text-orange-600">
              <Sparkles className="h-4 w-4" />
              <span className="text-xs font-bold uppercase tracking-[0.25em]">¿Qué nos hace diferentes?</span>
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">
              Cuatro pilares que sostienen la confianza en cada compra
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <DiffCard
              testid="qs-diff-validated"
              icon={<BadgeCheck className="h-6 w-6" />}
              title="Vendedores validados"
              body="Trabajamos para verificar la identidad y legitimidad de quienes comercializan dentro de la plataforma."
            />
            <DiffCard
              testid="qs-diff-safe"
              icon={<ShieldCheck className="h-6 w-6" />}
              title="Compras más seguras"
              body="Diseñamos procesos que buscan reducir riesgos y brindar mayor tranquilidad durante la compra."
            />
            <DiffCard
              testid="qs-diff-transparent"
              icon={<Eye className="h-6 w-6" />}
              title="Transparencia"
              body="Promovemos información clara sobre productos, vendedores, precios y condiciones de compra."
            />
            <DiffCard
              testid="qs-diff-support"
              icon={<MessageSquareWarning className="h-6 w-6" />}
              title="Soporte y PQR"
              body="Contamos con canales para que nuestros usuarios puedan reportar inconvenientes, presentar solicitudes y recibir atención."
            />
          </div>
        </div>
      </section>

      {/* ═══════════════ VISION ═══════════════ */}
      <section data-testid="qs-vision" className="bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 items-start">
            <div>
              <div className="inline-flex items-center gap-2 text-orange-600">
                <Compass className="h-5 w-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Nuestra visión</span>
              </div>
              <div className="mt-3 h-1 w-16 bg-orange-500" />
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 leading-snug">
                “Convertirnos en una de las plataformas de referencia en
                Colombia para la compra y venta de repuestos automotrices,
                reconocida por la confianza que genera en compradores y
                vendedores.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ CTA ═══════════════ */}
      <section data-testid="qs-cta" className="bg-slate-900 text-white relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-[600px] rounded-full bg-orange-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            Tu vehículo merece repuestos.{' '}
            <span className="text-orange-400">Tú mereces confianza.</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/catalogo"
              data-testid="qs-cta-catalog"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-slate-900 font-bold px-6 py-3 rounded-none transition-colors"
            >
              <ShoppingBag className="h-5 w-5" /> Explorar catálogo
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/vendedor/onboarding"
              data-testid="qs-cta-seller"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-bold px-6 py-3 rounded-none transition-colors"
            >
              <Store className="h-5 w-5" /> Quiero ser vendedor
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function DiffCard({ testid, icon, title, body }) {
  return (
    <div
      data-testid={testid}
      className="group bg-white border border-slate-200 p-6 hover:border-slate-900 hover:shadow-sm transition-all"
    >
      <div className="inline-flex h-11 w-11 items-center justify-center bg-slate-900 text-orange-400 group-hover:bg-orange-500 group-hover:text-slate-900 transition-colors">
        {icon}
      </div>
      <h3 className="mt-4 font-display text-lg font-black tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
